import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Spinner, useToast } from "@aidkitorg/component-library";
import { AdminPanelPage } from "../AdminPanelPage";
import { useLocalizedStrings } from "../../Localization";
import { AuditLogsDataTable } from "./components/AuditLogsDataTable";
import { useUserAllowed } from "../../Components/PermissionsUtil";
import { useAuditLogsConfig } from '../shared/hooks/useAdminPanelPageConfig';
import { useAuditLogs, VisibleColumnsConfig } from "../../Contexts/AuditLogsContext";
import { AuditLogsExportModal } from "./components/AuditLogsExportModal";

export const AuditLogsPage = () => {
  const L = useLocalizedStrings();
  const { toast, dismiss } = useToast();
  const isAdmin = useUserAllowed(["admin"]);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Fetch page configuration using the hook
  const {
    pageConfig,
    isLoading: isConfigLoading
  } = useAuditLogsConfig();

  const { exportJob, isExporting } = useAuditLogs();

  // Compute visible columns from pageConfig for export
  const visibleColumns = useMemo((): VisibleColumnsConfig => {
    if (!pageConfig?.defaultTableColumns) {
      // If no config, include all columns
      return {
        timestamp: true,
        eventType: true,
        userName: true,
        userId: true,
        userEmail: true,
      };
    }

    const columns = pageConfig.defaultTableColumns;
    return {
      timestamp: columns.timestamp !== 'hidden',
      eventType: columns.eventType !== 'hidden',
      userName: columns.userName !== 'hidden',
      userId: columns.userId !== 'hidden',
      userEmail: columns.userEmail !== 'hidden',
    };
  }, [pageConfig]);

  // Track export state with ref to avoid race conditions
  const exportStateRef = useRef<{
    toastId: string | null;
    toastForTaskId: string | null;
    downloadedTaskId: string | null;
  }>({ toastId: null, toastForTaskId: null, downloadedTaskId: null });

  const triggerDownload = useCallback((url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Handle export job status changes
  useEffect(() => {
    if (!exportJob) return;

    const state = exportStateRef.current;

    // Extract download info from result
    const resultData = exportJob.result?.data || exportJob.result;
    const downloadUrl = resultData?.downloadUrl;
    const filename = resultData?.filename;
    const errorMessage = exportJob.result?.error;

    // Show "Preparing" toast when export starts
    if ((exportJob.status === 'QUEUED' || exportJob.status === 'STARTED') &&
        state.toastForTaskId !== exportJob.taskId) {
      if (state.toastId) {
        dismiss(state.toastId);
      }

      const { id } = toast({
        title: L.admin_panel.audit_logs.export_preparing,
        description: L.admin_panel.audit_logs.export_preparing_desc,
        variant: 'default',
        icon: <Spinner size="xsmall" />,
        autoClose: false,
      });
      state.toastId = id;
      state.toastForTaskId = exportJob.taskId;
    }

    // Handle completed export
    if (exportJob.status === 'FINISHED' && downloadUrl && state.downloadedTaskId !== exportJob.taskId) {
      state.downloadedTaskId = exportJob.taskId;

      if (state.toastId) {
        dismiss(state.toastId);
        state.toastId = null;
        state.toastForTaskId = null;
      }

      toast({
        title: L.admin_panel.audit_logs.export_ready,
        description: L.admin_panel.audit_logs.export_ready_desc,
        variant: 'success',
        duration: 3000,
      });

      triggerDownload(downloadUrl, filename || 'audit-logs.zip');
    } else if (exportJob.status === 'FAILED' && state.downloadedTaskId !== exportJob.taskId) {
      state.downloadedTaskId = exportJob.taskId;

      if (state.toastId) {
        dismiss(state.toastId);
        state.toastId = null;
        state.toastForTaskId = null;
      }

      toast({
        title: L.admin_panel.audit_logs.export_failed,
        description: errorMessage || L.admin_panel.audit_logs.export_failed_desc,
        variant: 'error',
        autoClose: false,
      });
    }
  }, [exportJob, toast, dismiss, triggerDownload, L]);

  const showExportButton = isAdmin && pageConfig?.exportButton !== 'hidden';

  const pageActionButtons = showExportButton ? [
    <Button
      key="export"
      variant="secondary"
      onClick={() => setExportModalOpen(true)}
      isDisabled={isExporting}
      icon={<Icon name="ArrowDownTrayIcon" type="solid" role="decorative" />}
    >
      {isExporting ? L.admin_panel.audit_logs.exporting : L.admin_panel.audit_logs.export}
    </Button>
  ] : [];

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.audit_logs}
      description={L.admin_panel.audit_logs.description}
      pageActionButtons={pageActionButtons}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.audit_logs,
        },
      ]}
    >
      {isAdmin ? (
        <AuditLogsDataTable
          pageConfig={pageConfig}
          isConfigLoading={isConfigLoading}
        />
      ) : null}

      <AuditLogsExportModal
        open={exportModalOpen}
        setOpen={setExportModalOpen}
        visibleColumns={visibleColumns}
      />
    </AdminPanelPage>
  );
};
